import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { Layout } from "./components/layout/Layout";
import { AdminLayout } from "./components/layout/AdminLayout";
import { PlatformAdminGuard } from "./components/layout/PlatformAdminGuard";
import { LandingPage } from "./pages/public/LandingPage";
import { PrivacyPage } from "./pages/public/PrivacyPage";
import { TermsPage } from "./pages/public/TermsPage";
import { AnimalsArchivePage } from "./pages/animals/AnimalsArchivePage";
import { AnimalsPage } from "./pages/animals/AnimalsPage";
import { DashboardPage } from "./pages/dashboard/DashboardPage";
import { ExpensesPage } from "./pages/finance/ExpensesPage";
import { GuidePage } from "./pages/guide/GuidePage";
import { IrzPage } from "./pages/irz/IrzPage";
import { GoogleConsentPage } from "./pages/auth/GoogleConsentPage";
import { LoginPage } from "./pages/auth/LoginPage";
import { ForgotPasswordPage } from "./pages/auth/ForgotPasswordPage";
import { AccountDeletionCancelPage } from "./pages/auth/AccountDeletionCancelPage";
import { RegisterPage } from "./pages/auth/RegisterPage";
import { RegisterPendingPage } from "./pages/auth/RegisterPendingPage";
import { ResetPasswordPage } from "./pages/auth/ResetPasswordPage";
import { VerifyEmailPage } from "./pages/auth/VerifyEmailPage";
import { ReportsPage } from "./pages/dashboard/ReportsPage";
import { SettingsPage } from "./pages/settings/SettingsPage";
import { StatisticsPage } from "./pages/dashboard/StatisticsPage";
import { TagHistoryPage } from "./pages/tags/TagHistoryPage";
import { TagVerificationPage } from "./pages/tags/TagVerificationPage";
import { BreedingPage } from "./pages/reproduction/BreedingPage";
import { BirthsArchivePage } from "./pages/reproduction/BirthsArchivePage";
import { BirthsPage } from "./pages/reproduction/BirthsPage";
import { ReproductionHubPage } from "./pages/reproduction/ReproductionHubPage";
import { FreeTagsPage } from "./pages/reproduction/FreeTagsPage";
import { AdminAuditPage } from "./pages/admin/AdminAuditPage";
import { AdminDeletionsPage } from "./pages/admin/AdminDeletionsPage";
import { AdminOverviewPage } from "./pages/admin/AdminOverviewPage";
import { AdminPerformancePage } from "./pages/admin/AdminPerformancePage";
import { AdminSecurityPage } from "./pages/admin/AdminSecurityPage";
import { AdminToolsPage } from "./pages/admin/AdminToolsPage";
import { AdminUsersPage } from "./pages/admin/AdminUsersPage";

function RedirectPreserveSearch({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

export function App() {
  return (
    <BrowserRouter basename="/app">
      <Routes>
        <Route path="/welcome" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/google-consent" element={<GoogleConsentPage />} />
        <Route path="/register-pending" element={<RegisterPendingPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/account-deletion-cancel" element={<AccountDeletionCancelPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route element={<PlatformAdminGuard />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminOverviewPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/deletions" element={<AdminDeletionsPage />} />
            <Route path="/admin/audit" element={<AdminAuditPage />} />
            <Route path="/admin/security" element={<AdminSecurityPage />} />
            <Route path="/admin/performance" element={<AdminPerformancePage />} />
            <Route path="/admin/tools" element={<AdminToolsPage />} />
          </Route>
        </Route>
        <Route element={<Layout />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/statistics" element={<StatisticsPage />} />
          <Route path="/animals" element={<AnimalsPage />} />
          <Route path="/animals/archive" element={<AnimalsArchivePage />} />
          <Route path="/reproduction" element={<ReproductionHubPage />} />
          <Route path="/reproduction/breeding" element={<BreedingPage />} />
          <Route path="/reproduction/births" element={<BirthsPage />} />
          <Route path="/reproduction/archive" element={<BirthsArchivePage />} />
          <Route
            path="/reproduction/free-tags"
            element={<Navigate to="/resources" replace />}
          />
          <Route path="/resources" element={<FreeTagsPage />} />
          <Route path="/controls" element={<TagVerificationPage />} />
          <Route path="/controls/history" element={<TagHistoryPage />} />
          <Route
            path="/tag-verification"
            element={<RedirectPreserveSearch to="/controls" />}
          />
          <Route
            path="/tag-verification/history"
            element={<RedirectPreserveSearch to="/controls/history" />}
          />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/irz" element={<IrzPage />} />
          <Route path="/guide" element={<GuidePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
